import type { ReactNode } from "react";

import { useLang } from "../../lib/i18n";
import { appCopy } from "../../lib/i18n/app";
import { IconBack } from "./icon";

// === TopBar ===========================================================
// Three-column row, used on every non-home screen. Left = back affordance,
// center = uppercase mono section title, right = optional accent action.

export function TopBar({
  back,
  title,
  onBack,
  right,
}: {
  back?: string;
  title?: string;
  onBack?: () => void;
  right?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-3 px-0.5 py-1">
      <div className="flex-1 min-w-0">
        {back ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-[var(--ink-2)] hover:text-[var(--ink)] transition-colors"
          >
            <IconBack size={20} />
            <span className="truncate">{back}</span>
          </button>
        ) : null}
      </div>
      {title ? (
        <div
          className="text-[10px] font-semibold uppercase text-[var(--muted)] tracking-[0.12em] shrink-0"
          style={{ fontFamily: "var(--font-mono)" }}
        >
          {title}
        </div>
      ) : null}
      <div className="flex-1 min-w-0 flex items-center justify-end text-[13px] font-bold text-[var(--accent)]">
        {right}
      </div>
    </div>
  );
}

// === H1 (editorial display heading) ===================================
export function H1({
  children,
  accent,
  size = 42,
  className,
}: {
  children: ReactNode;
  accent?: ReactNode;
  size?: 42 | 56;
  className?: string;
}) {
  return (
    <h1
      className={className}
      style={{
        fontFamily: "var(--font-display)",
        fontSize: size,
        fontWeight: 800,
        letterSpacing: "-0.05em",
        lineHeight: 0.95,
        color: "var(--ink)",
      }}
    >
      {children}
      {accent !== undefined ? <span style={{ color: "var(--accent)" }}>{accent}</span> : null}
    </h1>
  );
}

// === Eyebrow (section label) =========================================
export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={className}
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        letterSpacing: "0.15em",
        color: "var(--muted)",
        fontWeight: 600,
        textTransform: "uppercase",
      }}
    >
      {children}
    </div>
  );
}

// === MonoMeta (inline metadata) ======================================
export function MonoMeta({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span
      className={className}
      style={{
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        color: "var(--muted)",
        letterSpacing: "0.04em",
      }}
    >
      {children}
    </span>
  );
}

// === Conf (confidence chip) ==========================================
export type ConfidenceLevel = "high" | "mid" | "low";

const CONF_MAP: Record<ConfidenceLevel, { bg: string; fg: string }> = {
  high: { bg: "var(--ok-bg)", fg: "var(--ok-fg)" },
  mid: { bg: "var(--warn-bg)", fg: "var(--warn-fg)" },
  low: { bg: "var(--bad-bg)", fg: "var(--bad-fg)" },
};

export function Conf({ level, label }: { level: ConfidenceLevel; label?: string }) {
  const { lang } = useLang();
  const t = appCopy[lang];
  const m = CONF_MAP[level];
  const defaultLabel =
    level === "high" ? t.confHigh : level === "mid" ? t.confMid : t.confLow;
  return (
    <span
      className="text-[11px] font-semibold tracking-[0.02em]"
      style={{
        padding: "3px 8px",
        borderRadius: 6,
        background: m.bg,
        color: m.fg,
      }}
    >
      {label ?? defaultLabel}
    </span>
  );
}

// Map a 0-1 numeric confidence (from AI schemas) to a ConfidenceLevel chip.
export function confLevelFromScore(score: number | null | undefined): ConfidenceLevel {
  if (score === null || score === undefined) return "mid";
  if (score >= 0.85) return "high";
  if (score >= 0.6) return "mid";
  return "low";
}

// === Chip (filter pill) ==============================================
export function Chip({
  children,
  active,
  onClick,
  disabled,
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`shrink-0 inline-flex items-center px-3 py-1.5 rounded-md text-[10px] font-bold uppercase tracking-[0.06em] transition-colors disabled:opacity-50 ${
        active
          ? "bg-[var(--ink)] text-[var(--bg)]"
          : "text-[var(--ink-2)] hover:bg-[var(--surface-2)]"
      }`}
      style={{
        fontFamily: "var(--font-mono)",
        boxShadow: active ? "none" : "inset 0 0 0 1px var(--nav-border)",
      }}
    >
      {children}
    </button>
  );
}

// === Stat (small KPI block) ==========================================
export function Stat({
  label,
  value,
}: {
  label: ReactNode;
  value: ReactNode;
}) {
  return (
    <div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9,
          letterSpacing: "0.12em",
          color: "var(--muted)",
          fontWeight: 600,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>
      <div
        className="tabular"
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 20,
          fontWeight: 700,
          color: "var(--ink)",
          letterSpacing: "-0.02em",
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

// === ScreenShell — full app frame =====================================
// Wraps every screen. Bottom nav padding is conditional — non-home screens
// without bottom nav pass `noBottomNav` to skip the pad.

export function ScreenFrame({
  children,
  noBottomNav,
}: {
  children: ReactNode;
  noBottomNav?: boolean;
}) {
  return (
    <div className="min-h-screen w-full" style={{ background: "var(--bg)", color: "var(--ink)" }}>
      <div
        className="mx-auto flex flex-col gap-0"
        style={{
          maxWidth: 480,
          padding: noBottomNav ? "16px 22px 32px" : "16px 22px 110px",
          minHeight: "100vh",
        }}
      >
        {children}
      </div>
    </div>
  );
}

// === PrimaryButton ====================================================
// Tangerine accent CTA with the canonical accent-glow shadow.
export function PrimaryButton({
  children,
  onClick,
  disabled,
  type = "button",
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  className?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`w-full inline-flex items-center justify-center gap-2 px-5 text-[14px] font-bold tracking-[0.02em] uppercase transition-all disabled:opacity-50 ${className ?? ""}`}
      style={{
        padding: "16px 20px",
        borderRadius: 12,
        background: "var(--accent)",
        color: "var(--accent-ink)",
        boxShadow: "var(--shadow-cta)",
      }}
    >
      {children}
    </button>
  );
}

// === SecondaryButton ==================================================
export function SecondaryButton({
  children,
  onClick,
  disabled,
  type = "button",
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  type?: "button" | "submit";
  className?: string;
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`w-full inline-flex items-center justify-center gap-2 px-5 text-[14px] font-semibold tracking-[0.02em] transition-colors disabled:opacity-50 ${className ?? ""}`}
      style={{
        padding: "14px 20px",
        borderRadius: 12,
        background: "transparent",
        color: "var(--ink)",
        boxShadow: "inset 0 0 0 1px var(--nav-border)",
      }}
    >
      {children}
    </button>
  );
}

// === Card =============================================================
export function Card({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={className}
      style={{
        background: "var(--surface)",
        borderRadius: 14,
        padding: 16,
        boxShadow: "var(--shadow-card)",
      }}
    >
      {children}
    </div>
  );
}

// === FormField ========================================================
// Standardized label + input pair used in all forms.
export function FormField({
  label,
  children,
  hint,
}: {
  label: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
}) {
  return (
    // biome-ignore lint/a11y/noLabelWithoutControl: <label> wraps an input child rendered by caller
    <label className="flex flex-col gap-1.5">
      <Eyebrow>{label}</Eyebrow>
      {children}
      {hint ? (
        <span className="text-[12px] text-[var(--muted)]" style={{ marginTop: 2 }}>
          {hint}
        </span>
      ) : null}
    </label>
  );
}

// === Input (shared shell) =============================================
import type { InputHTMLAttributes, TextareaHTMLAttributes } from "react";

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`outline-none transition-shadow ${props.className ?? ""}`}
      style={{
        height: 44,
        padding: "0 14px",
        background: "var(--surface)",
        color: "var(--ink)",
        borderRadius: 10,
        boxShadow: "inset 0 0 0 1px var(--nav-border)",
        // 16px to defuse the iOS Safari / WKWebView auto-zoom-on-focus bug
        // (computed font-size < 16 → zoom in, never zooms back out). The
        // theme.css @supports rule sets 16px globally for iOS already, but
        // inline style has higher specificity than CSS layers, so this
        // primitive must hard-code 16px to win. A 2px bump from the old
        // 14px design token; intentional cross-platform consistency.
        fontSize: 16,
        ...(props.style ?? {}),
      }}
    />
  );
}

export function Textarea({ rows = 4, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      rows={rows}
      className={`outline-none transition-shadow resize-none ${props.className ?? ""}`}
      style={{
        padding: "12px 14px",
        background: "var(--surface)",
        color: "var(--ink)",
        borderRadius: 10,
        boxShadow: "inset 0 0 0 1px var(--nav-border)",
        // 16px — same iOS auto-zoom reasoning as Input above.
        fontSize: 16,
        lineHeight: 1.5,
        ...(props.style ?? {}),
      }}
    />
  );
}

// === AiFooter — universal disclaimer ==================================
export function AiFooter({ children }: { children?: ReactNode }) {
  const { lang } = useLang();
  const t = appCopy[lang];
  return (
    <div
      className="mt-4 px-3 py-2.5 rounded-lg flex items-center gap-2"
      style={{
        background: "var(--surface-2)",
        color: "var(--muted)",
        fontSize: 11,
      }}
    >
      <span
        className="px-1.5 py-0.5 rounded text-[9px] font-bold"
        style={{
          background: "var(--accent)",
          color: "var(--accent-ink)",
          fontFamily: "var(--font-mono)",
        }}
      >
        AI
      </span>
      {children ?? t.aiFooterDefault}
    </div>
  );
}
